// Shared bit-level parsing for the ISO 11784/11785 64 bit animal transponder code

const TOTAL_BITS = 64;
const BITS_PER_BYTE = 8;

export const ISO_11784_APPLICATION_INDICATOR_BIT_OFFSET = 0;
export const ISO_11784_DATA_BLOCK_FLAG_BIT_OFFSET = 15;
export const ISO_11784_COUNTRY_CODE_BIT_OFFSET = 16;
export const ISO_11784_COUNTRY_CODE_BIT_COUNT = 10;
export const ISO_11784_NATIONAL_ID_BIT_OFFSET = 26;
export const ISO_11784_NATIONAL_ID_BIT_COUNT = 38;

export interface Iso11784Fields {
  isAnimal: boolean;
  dataBlockFlag: boolean;
  countryCode: number;
  nationalId: bigint;
}

export const bytesToIso11784Value = (bytes: Uint8Array): bigint => {
  if (bytes.length !== 8) {
    throw new Error(
      `Expected an 8 byte ISO 11784 payload, got ${bytes.length}`,
    );
  }

  return bytes.reduce(
    (acc, byte) => (acc << BigInt(BITS_PER_BYTE)) | BigInt(byte),
    BigInt(0),
  );
};

// start/length use big-endian, MSB-first bit numbering: bit 0 is the MSB of the 64 bit value
const readBits = (value: bigint, start: number, length: number): bigint => {
  const shift = BigInt(TOTAL_BITS - start - length);
  const mask = (BigInt(1) << BigInt(length)) - BigInt(1);
  return (value >> shift) & mask;
};

// Reads the 64 bit value once and returns every field, rather than making callers re-derive
// each field (and re-walk the same bits) one call at a time.
export const parseIso11784Data = (value: bigint): Iso11784Fields => ({
  isAnimal:
    readBits(value, ISO_11784_APPLICATION_INDICATOR_BIT_OFFSET, 1) ===
    BigInt(1),
  dataBlockFlag:
    readBits(value, ISO_11784_DATA_BLOCK_FLAG_BIT_OFFSET, 1) === BigInt(1),
  countryCode: Number(
    readBits(
      value,
      ISO_11784_COUNTRY_CODE_BIT_OFFSET,
      ISO_11784_COUNTRY_CODE_BIT_COUNT,
    ),
  ),
  nationalId: readBits(
    value,
    ISO_11784_NATIONAL_ID_BIT_OFFSET,
    ISO_11784_NATIONAL_ID_BIT_COUNT,
  ),
});

export function formatEid(tagData: Iso11784Fields): string {
  return `${tagData.countryCode.toString().padStart(3, "0")} ${tagData.nationalId.toString().padStart(12, "0")}`;
}
